import { useCallback, useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import theme from 'theme_'
import type { Row } from 'react-table'
import { FormattedMessage, useIntl } from 'react-intl'
import type { HomeId } from '@racepointenergy/swc/scs/types'
import { ProcessUpdate } from '@racepointenergy/swc/scs/types/host'
import { dealerMonitorProcesses } from '@racepointenergy/swc/scs/dealer-monitor-processes'
import { dealerConfigureHostRestart } from '@racepointenergy/swc/scs/dealer-configure-host-restart'
import { dealerConfigureProcess } from '@racepointenergy/swc/scs/dealer-configure-process'
import { processEntityAdapter } from '@racepointenergy/swc/scs/features/host-processes'
import { dealerMonitorSystemUsage } from '@racepointenergy/swc/scs/dealer-monitor-system-usage'

import { CommonButton } from 'components/Button'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { Spinner } from 'components/Loader'
import SysMonGuard, { GuardRouterProps } from '../../SysMonGuard'
import { SpinnerContainer } from '../HomeStatus/SubInfo/ConnectedApps/styles'
import { HomeProcessTable } from './ProcessesTable'
import {
  ProcessesResetHardwareButton,
  ProcessesSystemMonitorHeader,
  ProcessesSystemMonitorHeaderButtons,
  ProcessesTableMonitorContainer,
  ProcessesTableMonitorItem,
} from './styles'
import { processMessages } from './messages'
import { commonMessages } from 'app/i18n/messages'
import ProcessInfoComponent from './ProcessInfoComponent'
import ProcessInfoMobileComponent from './ProcessInfoMobileComponent'
import { hasSystemAccessEnabled } from 'features/home/utils'
import { useAppSelector } from 'app/hooks'
import { selectedHomeSelector } from 'features/home/selectors'
import { H2 } from 'components/Typography'
import RequestSysAccessMessage from './RequestSysAccessMsg'
import { KebabMenu, KebabMenuItem } from 'components/KebabMenu'
import ProcessesStats from './ProcessesStats'
import { homeSoftwareVersionSelector } from 'features/home/selectors'
import { HomePasswordChange } from '..'

const processInitialState = processEntityAdapter.getInitialState()
const processSelectors = processEntityAdapter.getSelectors()

interface HomeProcessesProps {
  homeId: HomeId
}

function HomeProcesses({ homeId: id }: HomeProcessesProps) {
  const { formatMessage } = useIntl()
  const { data = processInitialState, isLoading } =
    dealerMonitorProcesses.useQuery({ id }, { refetchOnReconnect: true })
  const dealerMonitorSystem = dealerMonitorSystemUsage.useQuery({
    id,
  })

  const [logLevelUpdate, { status: logLevelStatus }] =
    dealerConfigureProcess.useMutation()
  const [updateConfigureHostRestart, { status: processStatus }] =
    dealerConfigureHostRestart.useMutation()

  const isTabletOrMore = useMediaQuery({ query: theme.devices.tablet })
  const isDesktopOrMore = useMediaQuery({ query: theme.devices.desktop })

  const currentHome = useAppSelector(selectedHomeSelector.data)

  const hasSystemAccess = hasSystemAccessEnabled(currentHome)

  const [isResetHardwareModalOpen, setResetHardwareModalOpen] = useState(false)
  const [isResetSoftwareModalOpen, setResetSoftwareModalOpen] = useState(false)
  const [isResetAllLogsModalOpen, setResetAllLogsModalOpen] = useState(false)

  const [selectedProcess, setSelectedProcess] = useState<string | null>()
  const [processInfo, setProcessInfo] = useState<ProcessUpdate>()
  const homeSoftwareVersion = useAppSelector(homeSoftwareVersionSelector)
  const homeSoftwareVersionMajor =
    homeSoftwareVersion.softwareVersionLabel?.substring(
      0,
      homeSoftwareVersion.softwareVersionLabel?.indexOf('.')
    )
  const versionNumber = currentHome?.softwareInfo?.version.number || 0


  const onSelect = useCallback(
    (row: Row<ProcessUpdate>) => {
      if (row && data && selectedProcess !== row.original.ApplicationName) {
        setSelectedProcess(row.original.ApplicationName)
      }
    },
    [data, selectedProcess]
  )

  useEffect(() => {
    if (data && selectedProcess) {
      setProcessInfo(data.entities[selectedProcess])
    }
  }, [selectedProcess, data])

  const resetAllLogsAction = () => {
    setResetAllLogsModalOpen(true)
  }
  const resetHardwareAction = () => {
    setResetHardwareModalOpen(true)
  }
  const resetSoftwareAction = () => {
    setResetSoftwareModalOpen(true)
  }

  const resetAllLogs = useCallback(() => {
    logLevelUpdate({
      id,
      reset: true,
    })
    setResetAllLogsModalOpen(false)
  }, [id, logLevelUpdate])

  const resetHardware = useCallback(() => {
    setResetHardwareModalOpen(false)
    updateConfigureHostRestart({
      id,
      hard: true,
    })
  }, [id, updateConfigureHostRestart])

  const resetSoftware = useCallback(() => {
    setResetSoftwareModalOpen(false)
    updateConfigureHostRestart({
      id,
      hard: false,
    })
  }, [id, updateConfigureHostRestart])

  const processesData = processSelectors.selectAll(data)

  const shouldRenderMobileKebab =
    processesData.length > 0 && !isTabletOrMore && hasSystemAccess

  const isMacHostOver11 =                                              
    currentHome?.hostType.startsWith('Mac') && Number(versionNumber) > 1100000200000

  return (
    <>
      {isLoading ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : (
        <>
          <ConfirmModal
            isOpen={isResetAllLogsModalOpen}
            title={formatMessage(processMessages.resetLogLevelModalTitle)}
            confirmBtnTitle={formatMessage(processMessages.confirmResetButton)}
            discardBtnTitle={formatMessage(commonMessages.cancel)}
            variant="primary"
            onConfirm={resetAllLogs}
            onDiscard={() => setResetAllLogsModalOpen(false)}
          />
          <ConfirmModal
            isOpen={isResetHardwareModalOpen}
            title={formatMessage(processMessages.rebootHardwareTitle)}
            confirmBtnTitle={formatMessage(processMessages.confirmRebootButton)}
            discardBtnTitle={formatMessage(commonMessages.cancel)}
            variant="primary"
            onConfirm={resetHardware}
            onDiscard={() => setResetHardwareModalOpen(false)}
          >
            <FormattedMessage
              {...processMessages.rebootHardwareBody}
              values={{ br: <br /> }}
            />
          </ConfirmModal>
          <ConfirmModal
            isOpen={isResetSoftwareModalOpen}
            title={formatMessage(processMessages.rebootSoftwareTitle)}
            confirmBtnTitle={formatMessage(processMessages.confirmRebootButton)}
            discardBtnTitle={formatMessage(commonMessages.cancel)}
            variant="primary"
            onConfirm={resetSoftware}
            onDiscard={() => setResetSoftwareModalOpen(false)}
          >
            <FormattedMessage
              {...processMessages.rebootSoftwareBody}
              values={{ br: <br /> }}
            />
          </ConfirmModal>
          <ProcessesSystemMonitorHeader>
            <H2> {formatMessage(processMessages.hostProcess)}</H2>
            {!hasSystemAccessEnabled(currentHome) && isDesktopOrMore && (
              <RequestSysAccessMessage />
            )}
            {shouldRenderMobileKebab && (
              <KebabMenu extraStyles={{ zIndex: '2' }}>
                <KebabMenuItem
                  title={formatMessage(processMessages.resetLogLevelButton)}
                  onClick={resetAllLogsAction}
                />
                <KebabMenuItem
                  title={formatMessage(processMessages.hardwareRebootButton)}
                  onClick={resetHardwareAction}
                />
                <KebabMenuItem
                  title={formatMessage(processMessages.softwareRebootButton)}
                  onClick={resetSoftwareAction}
                />
              </KebabMenu>
            )}
            {processesData?.length > 0 &&
              isTabletOrMore &&
              hasSystemAccessEnabled(currentHome) && (
                <ProcessesSystemMonitorHeaderButtons>
                  <CommonButton
                    onClick={resetAllLogsAction}
                    disabled={logLevelStatus === 'pending'}
                  >
                    {formatMessage(processMessages.resetLogLevelButton)}
                  </CommonButton>
                  <ProcessesResetHardwareButton
                    onClick={resetHardwareAction}
                    disabled={processStatus === 'pending'}
                  >
                    {formatMessage(processMessages.hardwareRebootButton)}
                  </ProcessesResetHardwareButton>
                  <CommonButton
                    onClick={resetSoftwareAction}
                    disabled={processStatus === 'pending'}
                  >
                    {formatMessage(processMessages.softwareRebootButton)}
                  </CommonButton>
                </ProcessesSystemMonitorHeaderButtons>
              )}
          </ProcessesSystemMonitorHeader>
          {!hasSystemAccessEnabled(currentHome) && !isDesktopOrMore && (
            <RequestSysAccessMessage />
          )}
          {!selectedProcess && !isTabletOrMore && (
            <ProcessesStats stats={dealerMonitorSystem.data} />
          )}
          <ProcessesTableMonitorContainer>
            <ProcessesTableMonitorItem>
              <HomeProcessTable
                data={processesData}
                homeId={id}
                onSelect={onSelect}
              />
            </ProcessesTableMonitorItem>
            {selectedProcess && isTabletOrMore && (
              <ProcessInfoComponent
                value={processInfo}
                onClose={() => {
                  setSelectedProcess(null)
                }}
              />
            )}
            {!selectedProcess && isTabletOrMore && (
              <ProcessesStats stats={dealerMonitorSystem.data} />
            )}
            {selectedProcess && !isTabletOrMore && (
              <>
                <ProcessInfoMobileComponent
                  value={processInfo}
                  onClose={() => {
                    setSelectedProcess(null)
                  }}
                />
              </>
            )}
          </ProcessesTableMonitorContainer>
        </>
      )}
      {(Number(homeSoftwareVersionMajor) > 10 && !isMacHostOver11) && (
        <HomePasswordChange></HomePasswordChange>
      )}
    </>
  )
}

const HomeProcessesGuard = (props: GuardRouterProps) => {
  const { homeId } = props.match.params
  return (
    <SysMonGuard
      homeId={homeId}
      title={<FormattedMessage {...processMessages.hostProcess} />}
    >
      <HomeProcesses homeId={homeId} />
    </SysMonGuard>
  )
}

export default HomeProcessesGuard
